import { useEffect, type RefObject } from 'react'
import type { Highlight as HighlightData } from '../lib/types'

/**
 * Find a text string across potentially multiple DOM text nodes
 * within a container. Returns a Range or null.
 */
export function findTextRange(root: HTMLElement, searchText: string): Range | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  const nodes: { node: Text; start: number; end: number }[] = []
  let offset = 0

  let textNode: Text | null
  while ((textNode = walker.nextNode() as Text | null)) {
    const len = textNode.textContent?.length || 0
    nodes.push({ node: textNode, start: offset, end: offset + len })
    offset += len
  }

  const fullText = nodes.map((n) => n.node.textContent).join('')
  const matchIdx = fullText.indexOf(searchText)
  if (matchIdx === -1) return null

  const matchEnd = matchIdx + searchText.length
  const startInfo = nodes.find((n) => n.start <= matchIdx && n.end > matchIdx)
  const endInfo = nodes.find((n) => n.start < matchEnd && n.end >= matchEnd)
  if (!startInfo || !endInfo) return null

  const range = document.createRange()
  range.setStart(startInfo.node, matchIdx - startInfo.start)
  range.setEnd(endInfo.node, matchEnd - endInfo.start)
  return range
}

const HAS_HIGHLIGHT_API = typeof CSS !== 'undefined' && 'highlights' in CSS

/**
 * Apply inline text highlights using the CSS Custom Highlight API.
 * Falls back to no-op on browsers without support (~6%).
 *
 * @param containerRef - ref to the scrollable content container
 * @param highlights - array of Highlight objects from the store
 * @param contentKey - changes when DOM content updates (e.g. entries.length)
 */
export function useInlineHighlights(
  containerRef: RefObject<HTMLElement | null>,
  highlights: HighlightData[],
  contentKey: number,
) {
  useEffect(() => {
    if (!HAS_HIGHLIGHT_API) return

    // Small delay to let React finish rendering DOM after entries load
    const timer = setTimeout(() => {
      if (!containerRef.current) return

      const textsToHighlight = highlights.filter((h) => h.selected_text)
      if (textsToHighlight.length === 0) {
        ;(CSS as any).highlights.delete('scrivaro-hl')
        return
      }

      const ranges: Range[] = []
      for (const h of textsToHighlight) {
        const range = findTextRange(containerRef.current, h.selected_text)
        if (range) ranges.push(range)
      }

      if (ranges.length > 0) {
        const hl = new (window as any).Highlight(...ranges)
        ;(CSS as any).highlights.set('scrivaro-hl', hl)
      } else {
        ;(CSS as any).highlights.delete('scrivaro-hl')
      }
    }, 100)

    return () => {
      clearTimeout(timer)
    }
  }, [highlights, contentKey])
}
